// Real-time helpers for pages (Phase 15).
import * as React from 'react';
import { onLiveEvent, useRealtime, type LiveEvent } from '@/store/realtime';
import { useAuth } from '@/store/auth';

// Keep the WebSocket open while someone is signed in (used once, in the layouts).
export function useRealtimeConnection(): void {
  const token = useAuth((s) => s.token);
  React.useEffect(() => {
    if (!token) return;
    useRealtime.getState().connect(token);
    return () => useRealtime.getState().disconnect();
  }, [token]);
}

// Run fn when one of these events arrives (bursts are coalesced into one call per 250 ms).
export function useLiveRefresh(events: string[], fn: (e: LiveEvent) => void): void {
  const ref = React.useRef(fn);
  ref.current = fn;
  const key = events.join(',');
  React.useEffect(() => {
    const names = new Set(key.split(','));
    let timer: ReturnType<typeof setTimeout> | null = null;
    let last: LiveEvent | null = null;
    const off = onLiveEvent((e) => {
      if (!names.has(e.event)) return;
      last = e;
      if (timer) return;
      timer = setTimeout(() => { timer = null; if (last) ref.current(last); }, 250);
    });
    return () => { off(); if (timer) clearTimeout(timer); };
  }, [key]);
}

// Poll interval for a query: slower while live events keep the page fresh, normal otherwise.
export function usePoll(ms: number): number {
  const live = useRealtime((s) => s.status === 'live');
  return live ? Math.max(ms * 6, 30000) : ms;
}
